import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../client";
import type {
  Document,
  DocumentCreateRequest,
  DocumentDetail,
  DocumentEntityType,
  DocumentListRow,
  DocumentReasonOnly,
  DocumentType,
  DocumentUpdateRequest,
  DocumentValidation,
  DocumentValidationApplyRotationRequest,
  DocumentValidationApplyRotationResponse,
  DocumentValidationConfirmDelegationLogRequest,
  DocumentValidationConfirmDelegationLogResponse,
  DocumentValidationConfirmRequest,
  DocumentValidationConfirmResponse,
  DocumentVersionCompleteRequest,
  DocumentVersionCompleteResponse,
  DocumentVersionCreateRequest,
  PresignedDownloadResponse,
  PresignedUploadResponse,
} from "../types";

const DOCS_KEY = ["documents"] as const;

export interface DocumentFilters {
  entity_type?: DocumentEntityType;
  entity_id?: string;
  document_type?: DocumentType;
  search?: string;
  include_deleted?: boolean;
}

function buildQuery(filters?: DocumentFilters): string {
  if (!filters) return "";
  const params = new URLSearchParams();
  if (filters.entity_type) params.set("entity_type", filters.entity_type);
  if (filters.entity_id) params.set("entity_id", filters.entity_id);
  if (filters.document_type) params.set("document_type", filters.document_type);
  if (filters.search) params.set("search", filters.search);
  if (filters.include_deleted) params.set("include_deleted", "true");
  const s = params.toString();
  return s ? `?${s}` : "";
}

export function useDocuments(filters?: DocumentFilters) {
  return useQuery<DocumentListRow[]>({
    queryKey: [...DOCS_KEY, filters ?? {}],
    queryFn: () => apiRequest(`/api/documents${buildQuery(filters)}`),
  });
}

export function useDocument(id: string | undefined) {
  return useQuery<DocumentDetail>({
    queryKey: [...DOCS_KEY, id],
    queryFn: () => apiRequest(`/api/documents/${id}`),
    enabled: Boolean(id),
  });
}

export function useInitiateDocument() {
  return useMutation<PresignedUploadResponse, Error, DocumentCreateRequest>({
    mutationFn: (payload) =>
      apiRequest("/api/documents", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
  });
}

export function useViewVersion() {
  return useMutation<
    PresignedDownloadResponse,
    Error,
    { documentId: string; versionId: string }
  >({
    mutationFn: ({ documentId, versionId }) =>
      apiRequest(
        `/api/documents/${documentId}/versions/${versionId}/view`,
      ),
  });
}

export function useInitiateVersion(documentId: string) {
  return useMutation<
    PresignedUploadResponse,
    Error,
    DocumentVersionCreateRequest
  >({
    mutationFn: (payload) =>
      apiRequest(`/api/documents/${documentId}/versions`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
  });
}

export function useCompleteVersion() {
  const qc = useQueryClient();
  return useMutation<
    DocumentVersionCompleteResponse,
    Error,
    {
      documentId: string;
      versionId: string;
      payload: DocumentVersionCompleteRequest;
    }
  >({
    mutationFn: ({ documentId, versionId, payload }) =>
      apiRequest(
        `/api/documents/${documentId}/versions/${versionId}/complete`,
        { method: "POST", body: JSON.stringify(payload) },
      ),
    onSuccess: (_, { documentId }) => {
      qc.invalidateQueries({ queryKey: DOCS_KEY });
      qc.invalidateQueries({ queryKey: [...DOCS_KEY, documentId] });
    },
  });
}

export function useUpdateDocument(id: string) {
  const qc = useQueryClient();
  return useMutation<Document, Error, DocumentUpdateRequest>({
    mutationFn: (payload) =>
      apiRequest(`/api/documents/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DOCS_KEY });
      qc.invalidateQueries({ queryKey: [...DOCS_KEY, id] });
    },
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    { id: string; payload: DocumentReasonOnly }
  >({
    mutationFn: ({ id, payload }) =>
      apiRequest(`/api/documents/${id}`, {
        method: "DELETE",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: DOCS_KEY }),
  });
}

export function useRestoreDocument() {
  const qc = useQueryClient();
  return useMutation<
    Document,
    Error,
    { id: string; payload: DocumentReasonOnly }
  >({
    mutationFn: ({ id, payload }) =>
      apiRequest(`/api/documents/${id}/restore`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: DOCS_KEY }),
  });
}

/**
 * One-shot fetch for a download URL. Not a useQuery because each call
 * also writes a document_downloaded audit event server-side — invoking
 * this hook implicitly triggers the side effect.
 */
export function useDownloadVersion() {
  return useMutation<
    PresignedDownloadResponse,
    Error,
    { documentId: string; versionId: string }
  >({
    mutationFn: ({ documentId, versionId }) =>
      apiRequest(
        `/api/documents/${documentId}/versions/${versionId}/download`,
      ),
  });
}

/**
 * Override a FAILED validation with a reason. Server records the actor
 * + reason and emits a validation.overridden audit event. We invalidate
 * the document detail query so the drawer re-fetches the new status.
 */
export function useOverrideValidation(documentId: string) {
  const qc = useQueryClient();
  return useMutation<
    DocumentValidation,
    Error,
    { validationId: string; reason: string }
  >({
    mutationFn: ({ validationId, reason }) =>
      apiRequest(
        `/api/documents/${documentId}/validations/${validationId}/override`,
        {
          method: "POST",
          body: JSON.stringify({ reason }),
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...DOCS_KEY, documentId] }),
  });
}

/**
 * Confirm a delegation-log roster. For each entry: support staff
 * become LocationPersonnel rows; PI/Sub-I entries also create or
 * reuse Investigator + SiteDiseaseAreaInvestigator assignments.
 */
export function useConfirmDelegationLog(documentId: string) {
  const qc = useQueryClient();
  return useMutation<
    DocumentValidationConfirmDelegationLogResponse,
    Error,
    {
      validationId: string;
      payload: DocumentValidationConfirmDelegationLogRequest;
    }
  >({
    mutationFn: ({ validationId, payload }) =>
      apiRequest(
        `/api/documents/${documentId}/validations/${validationId}/confirm-delegation-log`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      ),
    // Confirming a roster writes LocationPersonnel + Investigator +
    // SiteDiseaseAreaInvestigator rows, and updates the validation's
    // extracted_value with confirmed_roster. That touches a bunch of
    // separate queries — the document itself, the per-participation
    // personnel list, the location's aggregated personnel, and the
    // ClinOps view (which counts investigators + drives roster
    // chips). Invalidate them all so the UI updates without a
    // manual page refresh.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...DOCS_KEY, documentId] });
      qc.invalidateQueries({ queryKey: ["participations"] });
      qc.invalidateQueries({ queryKey: ["locations"] });
      qc.invalidateQueries({ queryKey: ["sites"] });
    },
  });
}

/**
 * Apply operator-confirmed per-page rotations to a PDF in the rotation
 * review flow. Server creates a corrected v2 of the document, marks
 * it current, flips the orientation validation row to PASSED, and
 * re-runs content validators on v2.
 */
export function useApplyRotation(documentId: string) {
  const qc = useQueryClient();
  return useMutation<
    DocumentValidationApplyRotationResponse,
    Error,
    { validationId: string; payload: DocumentValidationApplyRotationRequest }
  >({
    mutationFn: ({ validationId, payload }) =>
      apiRequest(
        `/api/documents/${documentId}/validations/${validationId}/apply-rotation`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...DOCS_KEY, documentId] }),
  });
}

/**
 * Confirm a NEEDS_REVIEW validation with the operator's final values.
 * For GCP certs (today's only consumer) this also upserts the
 * InvestigatorCredential row that the rest of the system reads for
 * expiration tracking. Server emits a validation.passed audit event
 * with confirmed=true in the payload.
 */
export function useConfirmValidation(documentId: string) {
  const qc = useQueryClient();
  return useMutation<
    DocumentValidationConfirmResponse,
    Error,
    { validationId: string; payload: DocumentValidationConfirmRequest }
  >({
    mutationFn: ({ validationId, payload }) =>
      apiRequest(
        `/api/documents/${documentId}/validations/${validationId}/confirm`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...DOCS_KEY, documentId] }),
  });
}
